import { supabase } from '../supabaseClient';
import { WeeklyLearningGuideTrackColors, WeeklyLearningGuideTrackColorsUpsert } from '../types/domain';
import { canWriteSchoolScopedDataCache, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import {
  loadWeeklyLearningGuideTrackColorsCache,
  saveWeeklyLearningGuideTrackColorsCache,
} from '../utils/weeklyLearningGuideTrackColorsCache';

const SELECT = ['id', 'workspace_id', 'grade_id', 'academic_year', 'track_color_map', 'created_at', 'updated_at'].join(', ');

const CACHE_VERSION = 'v1';

function cacheKey(workspaceId: string, academicYear: number, gradeId: string): string {
  return `cp.weeklyLearningGuideTrackColors.${CACHE_VERSION}.${workspaceId}.${academicYear}.${gradeId}`;
}

function localFallbackKey(workspaceId: string, academicYear: number, gradeId: string): string {
  return `cp.weeklyLearningGuideTrackColors.local.${CACHE_VERSION}.${workspaceId}.${academicYear}.${gradeId}`;
}

function parseColorMap(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
    if (typeof k !== 'string' || typeof v !== 'string') return;
    const key = k.trim();
    const val = v.trim();
    if (key && val) out[key] = val;
  });
  return out;
}

function loadFromStorage(key: string): Record<string, string> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parseColorMap(parsed);
  } catch {
    return null;
  }
}

function saveToStorage(key: string, map: Record<string, string>, workspaceId: string, force = false): void {
  if (typeof window === 'undefined') return;
  if (!force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function isMissingTableError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes("Could not find the table 'public.weekly_learning_guide_track_colors'");
}

function rowFromDb(data: Record<string, unknown>): WeeklyLearningGuideTrackColors {
  return {
    id: String(data.id ?? ''),
    workspace_id: String(data.workspace_id ?? ''),
    grade_id: String(data.grade_id ?? ''),
    academic_year: Number(data.academic_year) || 0,
    track_color_map: parseColorMap(data.track_color_map),
    created_at: data.created_at ? String(data.created_at) : undefined,
    updated_at: data.updated_at ? String(data.updated_at) : undefined,
  };
}

export async function getWeeklyLearningGuideTrackColors(
  workspaceId: string,
  academicYear: number,
  gradeId: string
): Promise<Record<string, string>> {
  if (!workspaceId || !gradeId) return {};
  const cached = loadWeeklyLearningGuideTrackColorsCache(workspaceId, academicYear, gradeId);
  if (cached.found) return cached.map;
  try {
    const { data, error } = await supabase
      .from('weekly_learning_guide_track_colors')
      .select(SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('grade_id', gradeId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const row = data as { track_color_map?: unknown } | null;
    const map = parseColorMap(row?.track_color_map);
    saveWeeklyLearningGuideTrackColorsCache(workspaceId, academicYear, gradeId, map);
    return map;
  } catch (error) {
    if (!isMissingTableError(error)) throw error;
    const local = loadFromStorage(localFallbackKey(workspaceId, academicYear, gradeId)) ?? {};
    saveWeeklyLearningGuideTrackColorsCache(workspaceId, academicYear, gradeId, local, true);
    return local;
  }
}

export async function upsertWeeklyLearningGuideTrackColors(
  row: WeeklyLearningGuideTrackColorsUpsert
): Promise<WeeklyLearningGuideTrackColors> {
  if (!row.workspace_id || !row.grade_id || !Number.isInteger(row.academic_year)) {
    throw new Error('워크스페이스·학년·학년도가 필요합니다.');
  }
  const track_color_map = parseColorMap(row.track_color_map);
  const payload = {
    workspace_id: row.workspace_id,
    grade_id: row.grade_id,
    academic_year: row.academic_year,
    track_color_map,
    updated_at: new Date().toISOString(),
  };
  let saved: WeeklyLearningGuideTrackColors;
  try {
    const { data, error } = await supabase
      .from('weekly_learning_guide_track_colors')
      .upsert(payload, { onConflict: 'workspace_id,grade_id,academic_year' })
      .select(SELECT)
      .single();
    if (error) throw new Error(error.message);
    saved = rowFromDb((data as unknown as Record<string, unknown>) ?? {});
  } catch (error) {
    if (!isMissingTableError(error)) throw error;
    saveToStorage(
      localFallbackKey(row.workspace_id, row.academic_year, row.grade_id),
      track_color_map,
      row.workspace_id,
      true
    );
    saveWeeklyLearningGuideTrackColorsCache(row.workspace_id, row.academic_year, row.grade_id, track_color_map, true);
    const now = new Date().toISOString();
    return {
      id: `local-${row.workspace_id}-${row.grade_id}-${row.academic_year}`,
      workspace_id: row.workspace_id,
      grade_id: row.grade_id,
      academic_year: row.academic_year,
      track_color_map,
      created_at: now,
      updated_at: now,
    };
  }
  saveWeeklyLearningGuideTrackColorsCache(row.workspace_id, row.academic_year, row.grade_id, saved.track_color_map);
  await notifySchoolProgramMutation(row.workspace_id, ['weeklyGuide']);
  return saved;
}
